import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap'
import oikosApi from '../../../services/oikos'
import { notifyError, notifySuccess } from '../../../utils/notify'
import { selectValues } from '../../../utils/fc'
import Select from '../../Template/Input/Select'

const getEmptyFormData = () => ({
  orcItem_desc: '',
  orcRubr_id: null,
  categ_id: null,
  pf_id: null,
})

const normalizePositiveId = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function ModalFormOrcItem({
  isOpen = false,
  onClose = () => {},
  onSuccess = () => {},
  orcRubrId = null,
  categGrId = null,
  title = 'Novo item orcamentario',
}) {
  const [formData, setFormData] = useState(getEmptyFormData)
  const [selCategorias, setSelCategorias] = useState([])
  const [selPfs, setSelPfs] = useState([])
  const [selOrcRubrs, setSelOrcRubrs] = useState([])
  const [isLoadingFormOptions, setIsLoadingFormOptions] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const loadFormOptions = useCallback(async () => {
    if (categGrId == null) {
      setSelCategorias([])
      setSelPfs([])
      setSelOrcRubrs([])
      return
    }

    try {
      setIsLoadingFormOptions(true)

      const rubricasFilter = orcRubrId
        ? { orcRubr_ativo: 1, orcRubr_mae: orcRubrId }
        : null

      const [categorias, pfs, rubricas] = await Promise.all([
        selectValues('categ', 'categ_id', 'categ_nome', {
          categ_ativo: 1,
          categGr_id: categGrId,
        }),
        selectValues('pf', 'pf_id', 'pf_nomeExibicao', {
          pf_ativo: 1,
        }),
        rubricasFilter
          ? selectValues('orcRubr', 'orcRubr_id', 'orcRubr_nome', rubricasFilter)
          : Promise.resolve([]),
      ])

      const sortByLabel = (a, b) => {
        if (a.label < b.label) return -1
        if (a.label > b.label) return 1
        return 0
      }

      setSelCategorias((categorias || []).sort(sortByLabel))
      setSelPfs((pfs || []).sort(sortByLabel))
      setSelOrcRubrs((rubricas || []).sort(sortByLabel))
    } catch (e) {
      notifyError('Erro ao carregar opcoes do formulario.')
      console.log('Erro ao carregar opcoes do ModalFormOrcItem:', e)
    } finally {
      setIsLoadingFormOptions(false)
    }
  }, [categGrId, orcRubrId])

  useEffect(() => {
    if (!isOpen) return

    setFormData(getEmptyFormData())
    loadFormOptions()
  }, [isOpen, loadFormOptions])

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const handleSaveOrcItem = async () => {
    if (!formData.orcItem_desc?.trim()) {
      notifyError('Preencha a descricao do item.')
      return
    }

    if (!formData.categ_id) {
      notifyError('Selecione a categoria.')
      return
    }

    if (!formData.pf_id) {
      notifyError('Selecione o responsavel.')
      return
    }

    const selectedOrcRubrId = normalizePositiveId(formData.orcRubr_id)

    if (!selectedOrcRubrId) {
      notifyError('Selecione a rubrica do projeto.')
      return
    }

    if (!orcRubrId) {
      notifyError('Nao foi possivel identificar a rubrica do projeto.')
      return
    }

    try {
      setIsSaving(true)

      await oikosApi.post('/', {
        action: 'insert',
        table: 'orcItem',
        data: {
          orcItem_desc: formData.orcItem_desc.trim(),
          categ_id: formData.categ_id,
          pf_id: formData.pf_id,
          orcRubr_id: selectedOrcRubrId,
          orcItem_ativo: 1,
        },
      })

      if (onSuccess) {
        await onSuccess()
      }

      setFormData(getEmptyFormData())
      onClose()
      notifySuccess('Item orcamentario criado com sucesso.')
    } catch (e) {
      notifyError('Erro ao criar item orcamentario.')
      console.log('Erro ao inserir orcItem:', e)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>{title}</ModalHeader>
      <ModalBody>
        {isLoadingFormOptions ? (
          <div className="d-flex align-items-center">
            <Spinner size="sm" className="mr-2" />
            <span>Carregando opcoes...</span>
          </div>
        ) : (
          <>
            <FormGroup>
              <Label>Associe a uma rubrica do Projeto:</Label>
              <Select
                value={selOrcRubrs.find((option) => String(option.value) === String(formData.orcRubr_id)) || null}
                options={selOrcRubrs}
                onChange={(option) => handleFormChange('orcRubr_id', option?.value ?? null)}
                placeholder="Selecione uma rubrica do projeto"
              />
            </FormGroup>

            <FormGroup>
              <Label>Descricao</Label>
              <Input
                value={formData.orcItem_desc}
                onChange={(e) => handleFormChange('orcItem_desc', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Categoria</Label>
              <Select
                value={selCategorias.find((option) => Number(option.value) === Number(formData.categ_id)) || null}
                options={selCategorias}
                onChange={(option) => handleFormChange('categ_id', option?.value || null)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Responsavel</Label>
              <Select
                value={selPfs.find((option) => Number(option.value) === Number(formData.pf_id)) || null}
                options={selPfs}
                onChange={(option) => handleFormChange('pf_id', option?.value || null)}
              />
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button color="primary" onClick={handleSaveOrcItem} disabled={isSaving || isLoadingFormOptions}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ModalFormOrcItem
